import React, { useMemo, useState } from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import MultiTrendChart from "../../Components/Charts/MultiTrendChart";
import { Pager } from "../DataIntegration/shared";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  DownloadIcon,
  FileIcon,
  FilterIcon,
  MoreIcon,
  SearchIcon,
  SessionIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SortIcon,
  UsersIcon,
} from "../../Components/Icons";
import {
  ACTIONS,
  AUDIT_LOGS,
  LOGIN_DAYS,
  LOGIN_MAX,
  LOGIN_SERIES,
  MODULES,
  SECURITY_ACTIONS,
  SECURITY_EVENTS,
  SECURITY_STATS,
  SEVERITY_BREAKDOWN,
  TOP_USERS,
  TOTAL_ALERTS,
  TOTAL_EVENTS,
  USERS,
} from "./data";
import "./Security.css";

const STAT_ICONS = {
  shield: ShieldCheckIcon,
  check: CheckCircleIcon,
  alert: AlertTriangleIcon,
  users: UsersIcon,
  session: SessionIcon,
};

const ACTION_ICONS = {
  log: FileIcon,
  alert: AlertTriangleIcon,
  users: UsersIcon,
  session: SessionIcon,
  settings: SettingsIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/[\s/&]+/g, "-")}`;

export default function Security() {
  const [search, setSearch] = useState("");
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const [user, setUser] = useState("");
  const [sortAsc, setSortAsc] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = AUDIT_LOGS.filter((log) => {
      if (
        term &&
        !`${log.user} ${log.action} ${log.module} ${log.details}`
          .toLowerCase()
          .includes(term)
      ) {
        return false;
      }
      if (module && log.module !== module) return false;
      if (action && log.action !== action) return false;
      if (user && log.user !== user) return false;
      return true;
    });

    // Rows arrive newest first; the header toggle reverses that.
    return sortAsc ? [...filtered].reverse() : filtered;
  }, [search, module, action, user, sortAsc]);

  const resetPage = () => setPage(1);

  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Security &amp; Audit Logs</h1>
          <p className="page__subtitle">
            Monitor system activity, security events, and audit logs to ensure
            data integrity and compliance.
          </p>
        </div>
        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <DownloadIcon size={17} />
            Export Logs
          </button>
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <SettingsIcon size={17} />
            Audit Log Settings
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Security summary">
        {SECURITY_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="sec-stat h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="sec-stat__meta">
                  <p className="kpi__label">{card.label}</p>
                  <p className="sec-stat__value">
                    {card.value}
                    {card.delta && (
                      <span
                        className={`kpi__delta${
                          card.deltaTone === "warn" ? " kpi__delta--warn" : ""
                        }`}
                      >
                        <ArrowUpRightIcon size={12} />
                        {card.delta}
                      </span>
                    )}
                  </p>
                  <p className="sec-stat__foot">{card.foot}</p>
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {/* --------------------------------------------- audit logs + side -- */}
      <div className="sec-main">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Audit Logs</h2>
          </div>

          <div className="di-filters">
            <div className="filters__search">
              <input
                type="search"
                placeholder="Search by user, action, module or details..."
                aria-label="Search audit logs"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  resetPage();
                }}
              />
              <SearchIcon size={17} />
            </div>

            {[
              { value: module, set: setModule, label: "All Modules", options: MODULES },
              { value: action, set: setAction, label: "All Actions", options: ACTIONS },
              { value: user, set: setUser, label: "All Users", options: USERS },
            ].map((filter) => (
              <span className="select select--block" key={filter.label}>
                <select
                  value={filter.value}
                  aria-label={filter.label}
                  onChange={(event) => {
                    filter.set(event.target.value);
                    resetPage();
                  }}
                >
                  <option value="">{filter.label}</option>
                  {filter.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon size={16} />
              </span>
            ))}

            <button type="button" className="dgi-btn dgi-btn--ghost dgi-btn--sm">
              <FilterIcon size={16} />
              Filters
            </button>
          </div>

          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--audit">
              <thead>
                <tr>
                  <th>
                    <button
                      type="button"
                      className="th-sort"
                      onClick={() => setSortAsc((prev) => !prev)}
                      aria-label={`Sort by date, currently ${
                        sortAsc ? "oldest" : "newest"
                      } first`}
                    >
                      Date &amp; Time
                      <SortIcon size={14} />
                    </button>
                  </th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Module</th>
                  <th>Details</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((log) => (
                  <tr key={log.id}>
                    <td>
                      {log.date}
                      <span className="dgi-table__sub">{log.time}</span>
                    </td>
                    <td>{log.user}</td>
                    <td>
                      <span className={`code code--${log.action.toLowerCase()}`}>
                        {log.action}
                      </span>
                    </td>
                    <td>{log.module}</td>
                    <td className="sec-details">{log.details}</td>
                    <td>{log.ip}</td>
                    <td>
                      <span
                        className={`rowstat rowstat--${log.status.toLowerCase()}`}
                      >
                        <span className="rowstat__dot" />
                        {log.status}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="dgi-card__more"
                        aria-label={`Actions for event ${log.id}`}
                      >
                        <MoreIcon size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="dgi-table__empty">No events match these filters.</p>
          )}

          <Pager
            page={page}
            setPage={setPage}
            perPage={perPage}
            setPerPage={setPerPage}
            total={TOTAL_EVENTS}
            shown={rows.length}
            noun="events"
          />
        </section>

        <div className="stack">
          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Security Summary (This Month)</h2>
            </div>
            <DonutChart
              segments={SEVERITY_BREAKDOWN}
              total={TOTAL_ALERTS}
              totalLabel="Total Alerts"
            />
            <button type="button" className="dgi-card__link">
              View all security alerts <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title">Top Users by Activity (This Month)</h2>
            </div>
            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--compact">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Events</th>
                    <th>% of Total</th>
                  </tr>
                </thead>
                <tbody>
                  {TOP_USERS.map((row) => (
                    <tr key={row.user}>
                      <td>{row.user}</td>
                      <td>{row.events}</td>
                      <td>{row.share}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="dgi-card__link">
              View all users <ArrowRightIcon size={16} />
            </button>
          </section>
        </div>
      </div>

      {/* ------------------------------------------------- lower section -- */}
      <div className="sec-lower">
        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Security Events (This Month)</h2>
          </div>
          <div className="dgi-table-wrap">
            <table className="dgi-table dgi-table--events">
              <thead>
                <tr>
                  <th>Date &amp; Time</th>
                  <th>Event Type</th>
                  <th>Severity</th>
                  <th>User</th>
                  <th>Description</th>
                  <th>IP Address</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {SECURITY_EVENTS.map((event) => (
                  <tr key={event.id}>
                    <td>
                      {event.date}
                      <span className="dgi-table__sub">{event.time}</span>
                    </td>
                    <td>{event.type}</td>
                    <td>
                      <span className={chipClass(event.severity)}>
                        {event.severity}
                      </span>
                    </td>
                    <td>{event.user}</td>
                    <td className="sec-details">{event.description}</td>
                    <td>{event.ip}</td>
                    <td>
                      <span className={chipClass(event.status)}>
                        {event.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="dgi-card__link">
            View all security events <ArrowRightIcon size={16} />
          </button>
        </section>

        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Login Activity (Last 7 Days)</h2>
          </div>
          <MultiTrendChart
            labels={LOGIN_DAYS}
            series={LOGIN_SERIES}
            max={LOGIN_MAX}
            tickCount={4}
            formatTick={(value) => `${Math.round(value)}`}
            formatValue={(value) => `${value}`}
          />
          <button type="button" className="dgi-card__link">
            View full login report <ArrowRightIcon size={16} />
          </button>
        </section>

        <section className="dgi-card">
          <div className="dgi-card__header">
            <h2 className="dgi-card__title">Quick Actions</h2>
          </div>
          <ul className="qactions">
            {SECURITY_ACTIONS.map((item) => {
              const Icon = ACTION_ICONS[item.icon];
              return (
                <li key={item.key}>
                  <button type="button" className="qaction">
                    <span className="qaction__icon">
                      <Icon size={18} />
                    </span>
                    <span>
                      <span className="qaction__title">{item.title}</span>
                      <span className="qaction__text">{item.text}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </div>
  );
}
